import React, { useEffect, useState } from 'react';
import { Card, Typography, Table, message, Space, Select } from 'antd';
import ChargingHttpHandler from '../../shared/chargingHttpHandler';
import secureStorage from '../../shared/secureStorage';

const { Title } = Typography;

const ChargingMoneyManagement = () => {
  const [orders, setOrders] = useState([]);
  const [memberInfo, setMemberInfo] = useState({}); // 儲存會員資料 {account: {name, phone, carNumber, crCheck, account}}
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all'); // all | coupon | points

  // 根據訂單中的 userId 獲取對應的會員資料
  const fetchMemberInfoForOrders = async (orders) => {
    if (!orders || orders.length === 0) return;
    
    try {
      const httpHandler = new ChargingHttpHandler(false);
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
      
      // 取得所有不重複的 userId
      const uniqueUserIds = [...new Set(orders.map(order => order.userId).filter(Boolean))];
      console.log('需要查詢的 userId 列表:', uniqueUserIds);
      
      if (uniqueUserIds.length === 0) return;
      
      // 一次性獲取所有會員資料
      const response = await httpHandler.axios().get('/platform/get_member_info', {
        headers: { 'Authorization': `Bearer ${chargingAccessToken}` },
      });
      const memberMap = {};
      
      if (response.data && Array.isArray(response.data.data)) {
        response.data.data.forEach(member => {
          if (member.account) {
            memberMap[member.account] = {
              name: member.name || '未知',
              phone: member.phone || '未知',
              carNumber: member.car_number || '未知',
              crCheck: member.cr_check || 0,
              account: member.account
            };
          }
        });
        
        // 為訂單中的每個 userId 設定對應的會員資料
        uniqueUserIds.forEach(userId => {
          if (!memberMap[userId]) {
            // 如果找不到對應的會員資料，設定預設值
            memberMap[userId] = {
              name: '未知',
              phone: '未知',
              carNumber: '未知',
              crCheck: 0,
              account: userId
            };
          }
        });
      }
      
      setMemberInfo(memberMap);
    } catch (e) {
      console.error('獲取會員資料失敗', e);
    }
  };

  // 獲取訂單資料
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const httpHandler = new ChargingHttpHandler(false);
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
      
      const response = await httpHandler.axios().get('/platform/all-cdr-info', {
        headers: { 'Authorization': `Bearer ${chargingAccessToken}` },
      });

      if (response.data && Array.isArray(response.data.data)) {
        // 顯示所有訂單，不過濾
        const allOrders = response.data.data;

        setOrders(allOrders);
        
        // 根據訂單中的 userId 查詢對應的會員資料
        await fetchMemberInfoForOrders(allOrders);
      }
    } catch (error) {
      console.error('獲取訂單資料失敗', error);
      message.error('獲取訂單資料失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // 表格欄位定義
  const columns = [
    {
      title: '姓名',
      dataIndex: 'userId',
      key: 'name',
      width: 120,
      render: (userId) => {
        const member = memberInfo[userId];
        return member?.name || '未知';
      },
    },
    {
      title: '使用者ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 150,
      render: (userId) => {
        const member = memberInfo[userId];
        return member?.account || userId || '未知';
      },
    },
    {
      title: '應用商',
      dataIndex: ['chargeFeeRate', 'app_type'],
      key: 'appType',
      width: 120,
    },
    {
      title: '類型',
      key: 'type',
      width: 130,
      render: (_, record) => {
        const couponDeduct = Number(record.couponDeduct) || 0;
        const pointsDeduct = Number(record.pointsDeduct) || 0;
        if (couponDeduct > 0 && pointsDeduct > 0) return '優惠券+充電金';
        if (couponDeduct > 0) return '優惠券';
        if (pointsDeduct > 0) return '充電金';
        return '無折抵';
      }
    },
    {
      title: '優惠券折抵',
      dataIndex: 'couponDeduct',
      key: 'couponDeduct',
      width: 120,
      align: 'right',
      render: (v) => {
        const n = Number(v) || 0;
        return n > 0 ? `-$${n.toLocaleString()}` : '-';
      }
    },
    {
      title: '充電金折抵',
      dataIndex: 'pointsDeduct',
      key: 'pointsDeduct',
      width: 120,
      align: 'right',
      render: (v) => {
        const n = Number(v) || 0;
        return n > 0 ? `-$${n.toLocaleString()}` : '-';
      }
    },
    {
      title: '訂單金額',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      align: 'right',
      render: (v) => {
        const n = Number(v) || 0;
        return `$${n.toLocaleString()}`;
      }
    },
    {
      title: '訂單編號',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 180,
    },
  ];

  // 依照類型過濾顯示資料
  const filteredOrders = orders.filter((order) => {
    const couponDeduct = Number(order.couponDeduct) || 0;
    const pointsDeduct = Number(order.pointsDeduct) || 0;
    
    if (typeFilter === 'all') return true; // 全部
    if (typeFilter === 'coupon') return couponDeduct > 0; // 有優惠券
    if (typeFilter === 'points') return pointsDeduct > 0; // 有充電金
    if (typeFilter === 'none') return couponDeduct === 0 && pointsDeduct === 0; // 無折抵
    if (typeFilter === 'both') return couponDeduct > 0 && pointsDeduct > 0; // 兩者都有
    
    return true;
  });

  return (
    <div>
      <Card>        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: '#666' }}>
            共 {filteredOrders.length} 筆資料
          </div>
          <Space>
            <span style={{ color: '#666' }}>類型</span>
            <Select
              value={typeFilter}
              style={{ width: 160 }}
              onChange={setTypeFilter}
              options={[
                { label: '全部', value: 'all' },
                { label: '有優惠券', value: 'coupon' },
                { label: '有充電金', value: 'points' },
                { label: '兩者都有', value: 'both' },
                { label: '無折抵', value: 'none' },
              ]}
            />
          </Space>
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredOrders}
          loading={loading}
          rowKey="orderNo"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `資料筆數總計 ${total}筆 一頁顯示: ${range[1] - range[0] + 1}`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default ChargingMoneyManagement;
